import LaunchIcon from '@mui/icons-material/Launch';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { FC } from 'react';
import { clientAttributes } from 'services/types/client';
import type { TemplateResponse } from 'services/types/message';
import textToReactNodes from 'utils/textToReactNodes';

const getPreviewText = (
  text: string,
  bodyVariableExamples: string[] | Array<{ param_name: string; example: string }>
): string => {
  let previewText = text;

  // Check if we have named parameters or positional parameters
  if (bodyVariableExamples.length > 0 && typeof bodyVariableExamples[0] === 'object') {
    // Named parameters
    const namedParams = bodyVariableExamples as Array<{ param_name: string; example: string }>;
    namedParams.forEach((param) => {
      previewText = previewText.replaceAll(`{{${param.param_name}}}`, param.example || `{{${param.param_name}}}`);
    });
  } else {
    // Positional parameters (legacy)
    const positionalParams = bodyVariableExamples as string[];
    positionalParams.forEach((example, index) => {
      previewText = previewText.replace(`{{${index + 1}}}`, example || `{{${index + 1}}}`);
    });
  }

  return previewText;
};

const FormatPreviewText: FC<{ text: React.ReactNode[] }> = ({ text }) => {
  return (
    <>
      {text.map((node) => {
        const textPart = node?.toString();
        if (typeof textPart === 'string' && !textPart.includes('Object')) {
          return textPart.split(' ').map((word) => {
            if (word.startsWith('client.')) {
              return (
                <span key={word}>
                  <span style={{ backgroundColor: '#9EDDFF', padding: '0 2px', borderRadius: '4px' }}>
                    {clientAttributes.find((attribute) => attribute.value === word)?.title || word}
                  </span>{' '}
                </span>
              );
            }
            return `${word} `;
          });
        }
        return node;
      })}
    </>
  );
};

const PreviewHeader: FC<{
  headerText: string | null;
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerFileUrl: string | null;
}> = ({ headerText, headerFormat, headerFileUrl }) => {
  if (headerFormat === 'TEXT' && headerText) {
    return (
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {headerText}
      </Typography>
    );
  }

  if (headerFormat === 'IMAGE' && headerFileUrl) {
    return (
      <Box>
        <img src={headerFileUrl} alt="Header" style={{ maxWidth: '200px' }} />
      </Box>
    );
  }

  if (headerFormat === 'VIDEO' && headerFileUrl) {
    return (
      <Box>
        <video src={headerFileUrl} autoPlay muted loop style={{ maxWidth: '200px' }} />
      </Box>
    );
  }

  if (headerFormat === 'DOCUMENT' && headerFileUrl) {
    const filename = headerFileUrl.split('?')[0].split('/').pop();
    const displayName = filename?.replace(/^[a-z0-9]*-/, ''); // Remove the initial random string from the filename
    return (
      <Box>
        <a href={headerFileUrl} target="_blank" rel="noreferrer">
          <Typography variant="body2">Documento: {displayName}</Typography>
        </a>
      </Box>
    );
  }

  return null;
};

const PreviewBody: FC<{
  bodyText: string;
  bodyVariableExamples: string[] | Array<{ param_name: string; example: string }> | null;
}> = ({ bodyText, bodyVariableExamples }) => {
  let previewText = bodyText;
  if (bodyVariableExamples) {
    previewText = getPreviewText(bodyText, bodyVariableExamples);
  }

  return (
    <Typography
      variant="body1"
      sx={{
        whiteSpace: 'pre-line',
        marginBottom: '16px',
      }}
    >
      <FormatPreviewText text={textToReactNodes(previewText)} />
    </Typography>
  );
};

const PreviewFooter: FC<{ footerText: string }> = ({ footerText }) => {
  if (!footerText) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
      }}
    >
      {footerText}
    </Typography>
  );
};

const PreviewButtons: FC<{ buttons: TemplateResponse[number]['components'][number]['buttons'] }> = ({ buttons }) => {
  return (
    <Stack
      spacing={1}
      sx={{
        mt: 2,
      }}
    >
      {buttons?.map(
        (button, index) =>
          button.text && (
            <Button
              key={`${index + 1}`}
              variant="outlined"
              fullWidth
              size="small"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
              }}
              href={button.type === 'URL' ? button.url : undefined}
              startIcon={button.type === 'URL' ? <LaunchIcon /> : undefined}
            >
              {button.text}
            </Button>
          )
      )}
    </Stack>
  );
};

interface Props {
  cardTitle?: string;
  headerEnabled: boolean;
  headerText: string | null;
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | null;
  headerFileUrl: string | null;
  bodyText: string;
  bodyVariableExamples: string[] | Array<{ param_name: string; example: string }> | null;
  footerEnabled: boolean;
  footerText: string | null;
  buttons: TemplateResponse[number]['components'][number]['buttons'] | null;
  description?: React.ReactNode | null;
}

const PreviewTemplate: FC<Props> = ({
  cardTitle = 'Vista Previa',
  headerEnabled,
  headerText,
  headerFormat,
  headerFileUrl,
  bodyText,
  bodyVariableExamples,
  footerEnabled,
  footerText,
  buttons,
  description,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        position: 'sticky',
        top: 16,
      }}
    >
      <Typography variant="h6" gutterBottom>
        {cardTitle}
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          p: 2,
          minHeight: 200,
        }}
      >
        {headerEnabled && headerFormat && (
          <PreviewHeader headerText={headerText} headerFormat={headerFormat} headerFileUrl={headerFileUrl} />
        )}
        <PreviewBody bodyText={bodyText} bodyVariableExamples={bodyVariableExamples} />
        {footerEnabled && footerText && <PreviewFooter footerText={footerText} />}
        {buttons && buttons.length > 0 && <PreviewButtons buttons={buttons} />}
      </Box>
      {description}
    </Paper>
  );
};

export default PreviewTemplate;
